/**
 * +zone: Mossvale, one seamless open zone (docs/design/zones.md "Mossvale v2: the 1–10 journey"),
 * 864 × 810 yd. Themed areas along a road network in journey order, quiet travel country between
 * them, harder further from town:
 *
 *   town → Slime Hollows (1–2) → Boar Run / Brookside Farm (2–3) → Barrowfield and the Hollow
 *   Barrow (3–4) → back to town → Lakeside Outpost on Glimmermere (4–5) → Greyhorn Heights and
 *   Tallowdeep Mine (5–7) → Bramblewood and the Thornheart (7–9) → the Pale Rise (9–10) → the Tower Road.
 *
 * Heights come from mossvaleTerrain.ts (renderer only; the sim stays 2D). Waystones are attuned on
 * discovery. Respawn pacing lives in RESPAWN (one tunable place).
 */
import { registerAirFloor } from '@keystone/sim'; // +glide
import type { DungeonDef, FieldAreaDef, FieldDef, FieldNpcDef, FieldSecretDef, FieldSpawnDef, FieldWaystoneDef, Polygon, PortalDef, PropHint, Radians, Vec2 } from '@keystone/sim';
import { angleTo } from '../dungeons/geometry';
import { FIELD_NPC, MAP, MOB, PORTAL, TOWN_NPC, ZONE, ZONE_NPC } from './ids';
import { buildSkyways } from './mossvaleSky'; // +glide
import { BEACON, CRAGS, ELDER_TREE, FALLS, FORD, HILL, LAKE, LAKE_DEEP, PLATEAU_X0, PLATEAU_Y0, RAMP, RIVER, RIVER_HALF, SADDLE_Y, SLIME, TOWN, TOWN_R, ZONE_H, ZONE_W, distToPolyline, lakeE, lakeShape, mossvaleHeight, plateauRimX, plateauRimY, registerTerrainRoads, PELL_HOME, OUTCROPS, mossvaleFloor } from './mossvaleTerrain';

// ---------------------------------------------------------------------------
// Respawn pacing (the one tunable place; docs/design/zones.md "Respawns")
// ---------------------------------------------------------------------------

/**
 * Spawn timers for the open zone (ms, [min, max] rolled per death with seeded jitter, so a
 * cleared camp trickles back): normal mobs 45–90 s, elites / camp bosses 3–5 min, rares 10–15 min.
 * A slot never pops in while a hero stands within BLOCK_YD (+ the spawn radius) of its point (it
 * waits up to BLOCK_MAX_MS); clearing the same point continuously shaves 6% per recent kill off
 * the next delay, down to 30 s.
 */
export const RESPAWN = {
  normal: [45_000, 90_000] as const,
  elite: [180_000, 300_000] as const,
  rare: [600_000, 900_000] as const,
  blockYd: 20,
  blockMaxMs: 180_000,
  quicken: { perKill: 0.06, windowMs: 300_000, floorMs: 30_000 },
} as const;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const r1 = (v: number): number => Math.round(v * 10) / 10;
const r3 = (v: number): number => Math.round(v * 1000) / 1000;
const P = (x: number, y: number): Vec2 => ({ x, y });
const d2 = (a: Vec2, b: Vec2): number => Math.hypot(a.x - b.x, a.y - b.y);
const poly = (...pts: [number, number][]): Polygon => ({ points: pts.map(([x, y]) => ({ x, y })) });

/** Regular n-gon (CCW). */
function circle(c: Vec2, r: number, n = 16, phase = 0): Polygon {
  return { points: Array.from({ length: n }, (_, k) => ({ x: r1(c.x + Math.cos(phase + (k / n) * Math.PI * 2) * r), y: r1(c.y + Math.sin(phase + (k / n) * Math.PI * 2) * r) })) };
}

/** An oriented rectangle (w along `facing`'s right, d along it), CCW. */
function rect(c: Vec2, w: number, d: number, facing: number): Polygon {
  const fx = Math.cos(facing);
  const fy = Math.sin(facing);
  const rx = fy;
  const ry = -fx;
  const pts: [number, number][] = [
    [-w / 2, -d / 2],
    [w / 2, -d / 2],
    [w / 2, d / 2],
    [-w / 2, d / 2],
  ];
  const out = pts.map(([a, b]) => ({ x: r1(c.x + rx * a + fx * b), y: r1(c.y + ry * a + fy * b) }));
  // CCW
  let s = 0;
  for (let i = 0, j = out.length - 1; i < out.length; j = i++) s += out[j]!.x * out[i]!.y - out[i]!.x * out[j]!.y;
  return { points: s < 0 ? out.reverse() : out };
}

/** A strip of quads along a polyline (river, cliff bands), one polygon per segment. */
function strip(pts: readonly Vec2[], half: number): Polygon[] {
  const out: Polygon[] = [];
  for (let i = 0; i < pts.length - 1; i++) {
    const a = pts[i]!;
    const b = pts[i + 1]!;
    const len = Math.hypot(b.x - a.x, b.y - a.y);
    const nx = -(b.y - a.y) / len;
    const ny = (b.x - a.x) / len;
    // Extend each piece a little so consecutive pieces overlap at the joints.
    const ex = ((b.x - a.x) / len) * half * 0.6;
    const ey = ((b.y - a.y) / len) * half * 0.6;
    out.push({
      points: [
        { x: r1(a.x - ex + nx * half), y: r1(a.y - ey + ny * half) },
        { x: r1(a.x - ex - nx * half), y: r1(a.y - ey - ny * half) },
        { x: r1(b.x + ex - nx * half), y: r1(b.y + ey - ny * half) },
        { x: r1(b.x + ex + nx * half), y: r1(b.y + ey + ny * half) },
      ],
    });
  }
  return out;
}

const hash01 = (i: number, salt: number): number => {
  const s = Math.sin(i * 12.9898 + salt * 78.233) * 43758.5453;
  return s - Math.floor(s);
};

function pointIn(p: Vec2, pg: Polygon): boolean {
  const pts = pg.points;
  let inside = false;
  for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
    const a = pts[i]!;
    const b = pts[j]!;
    if (a.y > p.y !== b.y > p.y && p.x < ((b.x - a.x) * (p.y - a.y)) / (b.y - a.y) + a.x) inside = !inside;
  }
  return inside;
}

/** Path decals along a polyline every `step` yards (the renderer lays flagstones on them). */
function road(points: readonly [number, number][], step = 4): PropHint[] {
  const out: PropHint[] = [];
  for (let i = 1; i < points.length; i++) {
    const [ax, ay] = points[i - 1]!;
    const [bx, by] = points[i]!;
    const len = Math.hypot(bx - ax, by - ay);
    const facing = r3(Math.atan2(by - ay, bx - ax));
    const n = Math.max(1, Math.round(len / step));
    for (let k = 0; k < n; k++) out.push({ kind: 'dirt_path', pos: { x: r1(ax + ((bx - ax) * k) / n), y: r1(ay + ((by - ay) * k) / n) }, facing, scale: 3 });
  }
  return out;
}

const NORTH = Math.PI / 2;
const SOUTH = -Math.PI / 2;

// ---------------------------------------------------------------------------
// The boundary (one walkable polygon, CCW): a rounded, wobbling frame with bays, never a square
// ---------------------------------------------------------------------------

/** A narrow bump around angle `c` (radians). */
const bumpAt = (t: number, c: number, w: number): number => Math.exp(-((Math.atan2(Math.sin(t - c), Math.cos(t - c)) / w) ** 2));

function boundaryPoly(): Polygon {
  // +edge: a rounded frame (rounder in the south-west, north-west and south-east corners; the
  // north-east keeps its shoulder for the mine), mountain spurs reaching in, a canyon in the west and
  // a notch at the Tower Road (the high pass toward the Tower is the renderer's: edge.ts lowers the
  // mountains in the Tower's direction).
  const cx = ZONE_W / 2;
  const cy = ZONE_H / 2;
  const ax = 418;
  const ay = 392;
  const N = 180;
  const pts: Vec2[] = [];
  for (let k = 0; k < N; k++) {
    const t = (k / N) * Math.PI * 2;
    const pw = 0.25 + 0.15 * bumpAt(t, -2.36, 0.5) + 0.07 * bumpAt(t, 2.36, 0.45) + 0.14 * bumpAt(t, -0.79, 0.45);
    const f =
      1 +
      0.03 * Math.sin(5 * t + 1) +
      0.02 * Math.sin(9 * t + 3) +
      0.012 * Math.sin(17 * t + 2) +
      0.03 * Math.sin(3 * t + 5.3) +
      0.02 * Math.sin(2 * t + 4.0) -
      0.09 * bumpAt(t, -0.4, 0.08) -
      0.08 * bumpAt(t, 2.05, 0.08) -
      0.07 * bumpAt(t, -2.85, 0.06) -
      0.08 * bumpAt(t, -1.25, 0.07) -
      0.04 * bumpAt(t, 1.25, 0.05) +
      0.055 * bumpAt(t, Math.PI - 0.42, 0.03) +
      0.025 * bumpAt(t, 1.52, 0.05) -
      0.02 * bumpAt(t, 0.77, 0.08); // the mine's rock face stays the edge
    const c = Math.cos(t);
    const s = Math.sin(t);
    pts.push({ x: r1(cx + ax * Math.sign(c) * Math.abs(c) ** pw * f), y: r1(cy + ay * Math.sign(s) * Math.abs(s) ** pw * f) });
  }
  return { points: pts };
}
const BOUNDARY = boundaryPoly();

// ---------------------------------------------------------------------------
// Landmarks (the journey's stops)
// ---------------------------------------------------------------------------

const ELDER_POS: Vec2 = { x: TOWN.x - 7, y: TOWN.y + 5 };
/** New characters appear here, a few steps in front of Elder Rowan (and face him). */
/** +spawn: where the town square's arrivals stand (in front of Elder Rowan). */
const TOWN_ARRIVE: Vec2 = { x: TOWN.x - 5, y: TOWN.y + 1.5 };
/**
 * +spawn: new heroes start at Granny Pell's cottage on its rise at the Slime Hollows' southern edge,
 * facing the Tower across the valley (the north-east, through the high pass).
 */
export const ZONE_START: Vec2 = { x: PELL_HOME.x + 2, y: PELL_HOME.y + 13 };
/** +spawn: roughly where the renderer stands the Tower (the map centre + its direction × ~811 yd). */
export const TOWER_FAR: Vec2 = { x: r1(ZONE_W / 2 + Math.cos(Math.PI / 4) * 811), y: r1(ZONE_H / 2 + Math.sin(Math.PI / 4) * 811) };
/** +spawn: Granny Pell stands by her gate, facing the arriving hero. */
export const PELL_POS: Vec2 = { x: PELL_HOME.x + 8, y: PELL_HOME.y + 13.5 };
const BOAR_RUN: Vec2 = { x: 100, y: 332 };
const FARM: Vec2 = { x: 150, y: 262 };
/** The Hollow Barrow: at the edge of Barrowfield, north of town on the road to the Rise. */
const BARROW_MOUTH: Vec2 = { x: 394, y: 392 };
const BARROW_FACING = r3(angleTo(BARROW_MOUTH, { x: 420, y: 368 }));
const BARROW_MOUND: Vec2 = { x: r1(BARROW_MOUTH.x - Math.cos(BARROW_FACING) * 7), y: r1(BARROW_MOUTH.y - Math.sin(BARROW_FACING) * 7) };
const BARROWFIELD: Vec2 = { x: 436, y: 432 };
const OUTPOST: Vec2 = { x: 612, y: 330 };
const HEIGHTS: Vec2 = { x: 730, y: 610 };
const MINE: Vec2 = { x: 786, y: 740 };
const GROTTO: Vec2 = { x: 832, y: 622 };
const BRAMBLE_EDGE: Vec2 = { x: 250, y: 525 };
const BRAMBLE_DEEP: Vec2 = { x: 150, y: 610 };
const CAMP: Vec2 = { x: 140, y: 722 };
const LOOKOUT: Vec2 = { x: HILL.c.x, y: HILL.c.y + 6 };
const TOWER_GATE: Vec2 = { x: 462, y: 790 };

// ---------------------------------------------------------------------------
// Roads (journey order) and trails
// ---------------------------------------------------------------------------

const ROADS: [number, number][][] = [
  // ① Town → the Slime Hollows (west).
  [[TOWN.x - 25, TOWN.y], [362, 140], [312, 126], [280, 116]],
  // ② The Slime Hollows → Brookside Farm and the Boar Run.
  [[262, 136], [228, 186], [196, 232], [164, 258], [128, 292], [104, 322]],
  // ③ The farm → the north road → Barrowfield and the Barrow.
  [[164, 280], [206, 322], [270, 362], [334, 388], [384, 396]],
  // ④ Town → north (the backtrack) → the Barrowfield crossroads.
  [[TOWN.x, TOWN.y + 25], [434, 230], [428, 300], [422, 360], [420, 420]],
  // ⑤ Town → the east road → the Lakeside Outpost.
  [[TOWN.x + 25, TOWN.y], [500, 168], [548, 220], [584, 280], [604, 318]],
  // ⑥ The outpost → up the valley → Goat's Climb (a switchback) → the heights → the mine.
  [[620, 342], [630, 398], [636, 452], [634, 470], [642, 482], [632, 494], [642, 506], [634, 518], [640, 532], [660, 562], [700, 610], [740, 660], [766, 704], [784, 734]],
  // The ford: the valley road across the river into the eastern foothills.
  [[634, 410], [680, 412], [710, 411], [760, 422], [800, 434]],
  // ⑦ The crossroads → the west road → Bramblewood's edge → the Thornheart → the Briar Camp.
  [[420, 420], [372, 442], [320, 462], [272, 488], [236, 520], [204, 570], [178, 628], [156, 688], [142, 712]],
  // ⑧ The crossroads → up the Pale Rise → ⑨ the Tower Road.
  [[420, 420], [426, 482], [432, 540], [438, 598], [440, 652], [452, 720], [462, 786]],
  // Bramblewood → the Rise's western slope.
  [[204, 570], [262, 598], [322, 626], [372, 644]],
  // The heights → the saddle → the Rise (the high road).
  [[700, 610], [646, 680], [570, 708], [506, 694]],
];

/** Dirt side trails (no flagstones): into the dells, round the farm, along the shore, to secrets and lookouts. */
const TRAILS: [number, number][][] = [
  [[280, 116], [240, 96], [214, 70]],
  [[104, 322], [72, 364], [60, 400]],
  [[604, 318], [640, 286], [668, 262]],
  [[640, 532], [690, 520], [702, 506]],
  [[156, 688], [110, 700], [76, 702]],
  [[440, 652], [400, 690], [380, 716]],
  [[322, 230], [300, 262], [296, 290]],
  [[548, 220], [556, 300], [560, 380]],
  // +detail: side trails to the quiet country's points of interest (appended: the trails above keep their meander).
  [[433, 238], [396, 244], [356, 246]],
  [[430, 122], [420, 100], [404, 82]],
  [[478, 159], [488, 140], [494, 128]],
  [[431, 262], [452, 262], [461, 262]],
  [[558, 372], [538, 377], [527, 378]],
  [[350, 452], [354, 466]],
  [[676, 411], [683, 400]],
  // +spawn: the old south road from the Hollows to town, through the Ring of Hours.
  [[322, 104], [356, 86], [384, 80], [404, 82], [418, 102], [428, 124]],
];

/** Roads wander: every segment is cut into ~6 yd pieces nudged sideways by smooth noise (ends fixed). */
function meander(pts: readonly [number, number][], amp: number, salt: number): Vec2[] {
  const out: Vec2[] = [P(pts[0]![0], pts[0]![1])];
  let n = 0;
  for (let i = 1; i < pts.length; i++) {
    const [ax, ay] = pts[i - 1]!;
    const [bx, by] = pts[i]!;
    const len = Math.hypot(bx - ax, by - ay);
    const k = Math.max(1, Math.round(len / 6));
    const nx = -(by - ay) / len;
    const ny = (bx - ax) / len;
    for (let j = 1; j <= k; j++) {
      const t = j / k;
      n++;
      const env = j === k ? 0 : Math.sin(t * Math.PI);
      const off = (Math.sin(n * 0.33 + salt) * 0.65 + Math.sin(n * 0.12 + salt * 2.1) * 0.35) * amp * env;
      out.push(P(r1(ax + (bx - ax) * t + nx * off), r1(ay + (by - ay) * t + ny * off)));
    }
  }
  return out;
}

/** The Goat's Climb switchback stays exact (no meander on the ramp). */
const ROAD_LINES: Vec2[][] = ROADS.map((r, i) => (i === 5 ? r.map(([x, y]) => P(x, y)) : meander(r, 3.4, i * 1.7 + 0.4)));
const TRAIL_LINES: Vec2[][] = TRAILS.map((r, i) => meander(r, 2.2, i * 2.3 + 5));
registerTerrainRoads([...ROAD_LINES, ...TRAIL_LINES]); // +elev: roads cut into the slopes
const asPairs = (l: Vec2[]): [number, number][] => l.map((p) => [p.x, p.y]);
const roadHints = (): PropHint[] => [...ROAD_LINES.flatMap((r) => road(asPairs(r))), ...TRAIL_LINES.flatMap((r) => road(asPairs(r)).map((h) => ({ ...h, kind: 'trail', scale: 2.6 })))];
const nearRoad = (p: Vec2, d: number): boolean => [...ROAD_LINES, ...TRAIL_LINES].some((r) => distToPolyline(p, r) < d);

// ---------------------------------------------------------------------------
// Areas (sub-areas first, then their regions). Buffer country between them is in no area.
// ---------------------------------------------------------------------------

const AREAS: FieldAreaDef[] = [
  { id: 'mossvale', name: 'Mossvale', shape: circle(TOWN, TOWN_R + 5, 20), safe: true, biome: 'town', levelBand: [1, 10] },
  { id: 'lakeside_outpost', name: 'Lakeside Outpost', shape: circle(OUTPOST, 22, 16), region: 'glimmermere', safe: true, biome: 'town', levelBand: [4, 5], discoverKills: 2 },
  { id: 'slime_hollows', name: 'The Slime Hollows', shape: circle(SLIME, 88, 20), region: 'sunmeadow', levelBand: [1, 2], biome: 'dells', discoverKills: 1 },
  { id: 'brookside_farm', name: 'Brookside Farm', shape: circle(FARM, 30, 16), region: 'sunmeadow', levelBand: [2, 3], biome: 'farm', discoverKills: 2 },
  { id: 'boar_run', name: 'The Boar Run', shape: circle(BOAR_RUN, 82, 18), region: 'sunmeadow', levelBand: [2, 3], biome: 'farm', discoverKills: 2 },
  { id: 'hollow_barrow', name: 'The Hollow Barrow', shape: circle(BARROW_MOUND, 18, 16), region: 'barrowfield', levelBand: [3, 4], biome: 'ruins', discoverKills: 2 },
  { id: 'wisp_isle', name: 'Wisp Isle', shape: circle(LAKE.isle, LAKE.island + 4, 16), region: 'glimmermere', levelBand: [5, 5], biome: 'lake', discoverKills: 3 },
  { id: 'silverstep_falls', name: 'Silverstep Falls', shape: circle({ x: FALLS.x, y: FALLS.y - 22 }, 22, 16), region: 'greyhorn', levelBand: [5, 5], biome: 'rocky', discoverKills: 3 },
  { id: 'goats_climb', name: "Goat's Climb", shape: poly([RAMP.x0 - 6, RAMP.y0 - 6], [RAMP.x1 + 6, RAMP.y0 - 6], [RAMP.x1 + 6, RAMP.y1 + 4], [RAMP.x0 - 6, RAMP.y1 + 4]), region: 'greyhorn', levelBand: [5, 6], biome: 'rocky', discoverKills: 1 },
  { id: 'tallowdeep', name: 'Tallowdeep Mine', shape: circle({ x: MINE.x - 10, y: MINE.y - 10 }, 44, 16), region: 'greyhorn', levelBand: [6, 7], biome: 'rocky', discoverKills: 3 },
  { id: 'whisper_grotto', name: 'Whisper Grotto', shape: circle(GROTTO, 18, 16), region: 'greyhorn', levelBand: [6, 7], biome: 'rocky', discoverKills: 4 },
  { id: 'briar_camp', name: 'Briar Camp', shape: circle(CAMP, 38, 16), region: 'bramblewood', levelBand: [8, 9], biome: 'deepforest', discoverKills: 3 },
  { id: 'thornheart', name: 'The Thornheart', shape: poly([-20, 640], [236, 640], [250, 900], [-20, 900]), region: 'bramblewood', levelBand: [8, 9], biome: 'deepforest', discoverKills: 2 },
  { id: 'starfall_lookout', name: 'Starfall Lookout', shape: circle(LOOKOUT, 14, 16), region: 'pale_rise', levelBand: [10, 10], biome: 'ruins', discoverKills: 6 },
  { id: 'tower_road', name: 'The Tower Road', shape: circle(TOWER_GATE, 30, 16), region: 'pale_rise', levelBand: [10, 10], biome: 'ruins', discoverKills: 3 },
  // +spawn: landmarks on the road from the Hollows to town (small discovery moments, no mobs).
  { id: 'wayside_shrine', name: 'The Wayside Shrine', shape: circle({ x: 371, y: 133 }, 13, 12), levelBand: [1, 2], biome: 'meadow', discoverKills: 1 },
  { id: 'ring_of_hours', name: 'The Ring of Hours', shape: circle({ x: 395, y: 72 }, 16, 12), levelBand: [1, 2], biome: 'meadow', discoverKills: 1 },
  { id: 'hollowcrown', name: 'The Hollowcrown', shape: circle(ELDER_TREE, 26, 14), levelBand: [1, 3], biome: 'meadow', discoverKills: 1 },
  // Regions
  { id: 'pale_rise', name: 'The Pale Rise', shape: circle(HILL.c, 132, 24), levelBand: [9, 10], biome: 'ruins', discoverKills: 3 },
  { id: 'barrowfield', name: 'Barrowfield', shape: circle({ x: 424, y: 418 }, 78, 20), levelBand: [3, 4], biome: 'meadow', discoverKills: 2 },
  { id: 'greyhorn', name: 'Greyhorn Heights', shape: poly([598, 468], [680, 468], [690, 370], [900, 370], [900, 900], [560, 900], [560, 720], [598, 700]), levelBand: [5, 7], biome: 'rocky', discoverKills: 3 },
  { id: 'glimmermere', name: 'Glimmermere', shape: circle(LAKE.c, 128, 24), levelBand: [4, 5], biome: 'lake', discoverKills: 2 },
  { id: 'bramblewood', name: 'Bramblewood', shape: poly([-20, 440], [300, 440], [336, 500], [300, 640], [300, 900], [-20, 900]), levelBand: [7, 9], biome: 'forest', discoverKills: 2 },
  { id: 'sunmeadow', name: 'Sunmeadow', shape: poly([-20, -20], [350, -20], [350, 210], [300, 300], [240, 430], [-20, 430]), levelBand: [1, 3], biome: 'meadow', discoverKills: 1 },
];

/** The region (top-level area id) at a point. */
function regionOf(p: Vec2): string | null {
  const a = AREAS.find((x) => pointIn(p, x.shape));
  return a ? (a.region ?? a.id) : null;
}
const areaOf = (p: Vec2): string | null => AREAS.find((x) => pointIn(p, x.shape))?.id ?? null;

// ---------------------------------------------------------------------------
// NPCs, secrets, waystones, the barrow portal
// ---------------------------------------------------------------------------

const npcAt = (n: Omit<FieldNpcDef, 'facing'>, faceTo: Vec2): FieldNpcDef => ({ ...n, facing: r3(angleTo(n.pos, faceTo)) });
const T = (dx: number, dy: number): Vec2 => ({ x: TOWN.x + dx, y: TOWN.y + dy });
const O = (dx: number, dy: number): Vec2 => ({ x: OUTPOST.x + dx, y: OUTPOST.y + dy });

const NPCS: FieldNpcDef[] = [
  npcAt({ id: TOWN_NPC.elderRowan, name: 'Elder Rowan', title: 'Village Elder', role: 'questgiver', pos: ELDER_POS, model: 'elder' }, TOWN_ARRIVE),
  npcAt({ id: TOWN_NPC.hearthkeeper, name: 'Hearthkeeper Bryn', title: 'Keeper of the Hearth', role: 'questgiver', pos: T(7, -5), model: 'villager' }, TOWN),
  npcAt({ id: TOWN_NPC.bountyBoard, name: 'Bounty Board', title: 'Bounties', role: 'bounty_board', pos: T(7, 8), model: 'board' }, TOWN),
  npcAt({ id: TOWN_NPC.smithHilda, name: 'Hilda', title: 'Blacksmith', role: 'forge', pos: T(-16, -6), model: 'smith' }, TOWN),
  npcAt({ id: TOWN_NPC.dungeonBoard, name: 'Dungeon Board', title: 'Dungeons', role: 'dungeon_board', pos: T(-5, -12), model: 'board' }, TOWN),
  npcAt({ id: TOWN_NPC.quartermaster, name: 'Quartermaster Penn', title: 'Gear', role: 'gear', pos: T(13, 1), model: 'merchant' }, TOWN),
  npcAt({ id: TOWN_NPC.townGuard, name: 'Guard Aldric', title: 'Town Guard', role: 'guard', pos: T(3, 20), model: 'guard' }, T(0, 40)),
  npcAt({ id: TOWN_NPC.villagerMira, name: 'Mira', title: 'Flower Seller', role: 'villager', pos: T(-10, -5), model: 'villager' }, TOWN),
  npcAt({ id: TOWN_NPC.villagerTobin, name: 'Tobin', title: 'Farmhand', role: 'villager', pos: { x: FARM.x + 6, y: FARM.y - 4 }, model: 'villager' }, FARM),
  npcAt({ id: FIELD_NPC.towerWarden, name: 'Warden Ilsa', title: 'The Tower Road', role: 'questgiver', pos: { x: TOWER_GATE.x - 4, y: TOWER_GATE.y - 8 }, model: 'guard' }, { x: 452, y: 740 }),
  // The Lakeside Outpost: a fisher, its keeper, its own bounty board (the lake and the heights).
  npcAt({ id: ZONE_NPC.fisherDunn, name: 'Fisher Dunn', title: 'Glimmermere', role: 'villager', pos: O(12, -8), model: 'villager' }, LAKE.c),
  npcAt({ id: ZONE_NPC.outpostKeeper, name: 'Keeper Maren', title: 'Lakeside Outpost', role: 'villager', pos: O(-4, 4), model: 'guard' }, OUTPOST),
  npcAt({ id: ZONE_NPC.outpostBoard, name: 'Outpost Board', title: 'Bounties', role: 'bounty_board', pos: O(4, 9), model: 'board' }, OUTPOST),
  // Lore stones: the zone's history, at its corners and along the roads.
  npcAt({ id: ZONE_NPC.loreLake, name: 'Weathered Stone', title: 'Lore', role: 'lore', pos: O(-14, 22), model: 'lore' }, LAKE.c),
  npcAt({ id: ZONE_NPC.loreForest, name: 'Mossy Waystone', title: 'Lore', role: 'lore', pos: { x: 226, y: 552 }, model: 'lore' }, { x: 204, y: 570 }),
  npcAt({ id: ZONE_NPC.loreRise, name: 'Starfall Tablet', title: 'Lore', role: 'lore', pos: { x: LOOKOUT.x + 4, y: LOOKOUT.y + 3 }, model: 'lore' }, HILL.c),
  npcAt({ id: ZONE_NPC.loreHeights, name: "Miner's Shrine", title: 'Lore', role: 'lore', pos: { x: 724, y: 648 }, model: 'lore' }, { x: 740, y: 660 }),
  npcAt({ id: ZONE_NPC.loreRoad, name: 'Mile Stone', title: 'Lore', role: 'lore', pos: { x: 526, y: 186 }, model: 'lore' }, { x: 500, y: 168 }),
  npcAt({ id: ZONE_NPC.loreField, name: 'Sunken Plinth', title: 'Lore', role: 'lore', pos: { x: 300, y: 384 }, model: 'lore' }, { x: 270, y: 362 }),
];

/** Hidden chests (FieldDef.secrets), some out in the quiet country between the areas. */
const SECRETS: FieldSecretDef[] = [
  { id: 'falls_cache', pos: { x: FALLS.x - 10, y: FALLS.y - 22 }, kind: 'chest' },
  { id: 'grotto_cache', pos: { x: GROTTO.x + 4, y: GROTTO.y }, kind: 'chest' },
  { id: 'mine_cache', pos: { x: MINE.x + 6, y: MINE.y + 8 }, kind: 'chest' },
  { id: 'isle_cache', pos: { x: LAKE.isle.x + 3, y: LAKE.isle.y - 6 }, kind: 'chest' },
  { id: 'rise_ledge', pos: { x: 384, y: 720 }, kind: 'chest' },
  { id: 'thornheart_hollow', pos: { x: 78, y: 704 }, kind: 'chest' },
  { id: 'hollow_stump', pos: { x: 298, y: 292 }, kind: 'chest' },
  { id: 'old_well', pos: { x: 560, y: 384 }, kind: 'chest' },
  { id: 'dell_nook', pos: { x: 212, y: 66 }, kind: 'chest' },
];

/** Waystones (attuned on discovery; free travel between attuned stones). */
const WAYSTONES: FieldWaystoneDef[] = [
  { id: 'ws_town', name: 'Mossvale', pos: T(13, 17) },
  { id: 'ws_barrow', name: 'Barrowfield', pos: { x: 408, y: 352 } },
  { id: 'ws_outpost', name: 'Lakeside Outpost', pos: O(-16, -14) },
  { id: 'ws_climb', name: "Goat's Climb", pos: { x: 654, y: 548 } },
  { id: 'ws_bramble', name: 'Bramblewood', pos: { x: 264, y: 494 } },
  { id: 'ws_rise', name: 'The Rise', pos: { x: 418, y: 548 } },
];

/** Waystones stand in the world as NPCs (role 'waystone': the prompt, the click, the panel). */
NPCS.push(...WAYSTONES.map((w): FieldNpcDef => ({ id: w.id, name: 'Waystone', title: w.name, role: 'waystone', pos: w.pos, facing: SOUTH, model: 'waystone' })));

// ---------------------------------------------------------------------------
// +detail: points of interest in the quiet travel country (docs/design/zones.md "Points of interest"):
// each a sight, most with a lore stone or a hidden cache; no mobs (they stand in no area).
// ---------------------------------------------------------------------------

const HERMIT: Vec2 = { x: 470, y: 262 };
const STONE_RING: Vec2 = { x: 395, y: 72 };
const STONE_KING: Vec2 = { x: 506, y: 374 };
const ANGLER: Vec2 = { x: 686, y: 392 };
const OLD_CAMP: Vec2 = { x: 356, y: 477 };
const SHRINE: Vec2 = { x: 371, y: 131 };

/** The points of interest (centres and the radius kept clear of copses). */
const POIS: readonly { id: string; pos: Vec2; r: number }[] = [
  { id: 'hollowcrown', pos: ELDER_TREE, r: 30 },
  { id: 'old_beacon', pos: BEACON, r: 16 },
  { id: 'hermit_hut', pos: HERMIT, r: 14 },
  { id: 'stone_ring', pos: STONE_RING, r: 16 },
  { id: 'stone_king', pos: STONE_KING, r: 16 },
  { id: 'angler', pos: ANGLER, r: 12 },
  { id: 'old_camp', pos: OLD_CAMP, r: 12 },
  { id: 'wayside_shrine', pos: SHRINE, r: 8 },
];

NPCS.push(
  npcAt({ id: ZONE_NPC.loreElderTree, name: 'Hollowcrown', title: 'Lore', role: 'lore', pos: { x: ELDER_TREE.x + 12, y: ELDER_TREE.y - 8 }, model: 'lore' }, ELDER_TREE),
  npcAt({ id: ZONE_NPC.loreBeacon, name: 'Beacon Stone', title: 'Lore', role: 'lore', pos: { x: BEACON.x + 6, y: BEACON.y + 9 }, model: 'lore' }, BEACON),
  npcAt({ id: ZONE_NPC.loreRing, name: 'Ring of Hours', title: 'Lore', role: 'lore', pos: STONE_RING, model: 'lore' }, { x: 404, y: 82 }),
  npcAt({ id: ZONE_NPC.loreStatue, name: 'The Stone King', title: 'Lore', role: 'lore', pos: { x: STONE_KING.x + 15, y: STONE_KING.y + 6 }, model: 'lore' }, STONE_KING),
  npcAt({ id: ZONE_NPC.loreShrine, name: 'Wayside Shrine', title: 'Lore', role: 'lore', pos: { x: SHRINE.x + 6, y: SHRINE.y + 2 }, model: 'lore' }, { x: 372, y: 141 }),
  npcAt({ id: ZONE_NPC.loreCamp, name: 'Scratched Plank', title: 'Lore', role: 'lore', pos: { x: OLD_CAMP.x - 7, y: OLD_CAMP.y - 6 }, model: 'lore' }, OLD_CAMP),
);
SECRETS.push(
  { id: 'hollowcrown_roots', pos: { x: ELDER_TREE.x - 8, y: ELDER_TREE.y + 8 }, kind: 'chest' },
  { id: 'beacon_cellar', pos: { x: BEACON.x - 7, y: BEACON.y - 6 }, kind: 'chest' },
  { id: 'hermit_cache', pos: { x: HERMIT.x + 7, y: HERMIT.y + 4 }, kind: 'chest' },
  { id: 'kings_hand', pos: { x: STONE_KING.x - 12, y: STONE_KING.y - 10 }, kind: 'chest' },
  { id: 'anglers_tin', pos: { x: ANGLER.x - 9, y: ANGLER.y }, kind: 'chest' },
  { id: 'lost_pack', pos: { x: OLD_CAMP.x + 7, y: OLD_CAMP.y + 9 }, kind: 'chest' },
);

// +glide: the sky puzzles (mossvaleSky.ts): platforms and wind chutes, a richer chest on each top;
// the beginner one carries Granny Pell's note.
registerAirFloor(MAP.mossvale, mossvaleFloor);
const SKY = buildSkyways(mossvaleFloor);
SECRETS.push(...SKY.secrets);
const SKY_SIGN: Vec2 = { x: 400, y: 208 };
NPCS.push(npcAt({ id: ZONE_NPC.loreSkyway, name: "Granny Pell's Note", title: 'Lore', role: 'lore', pos: SKY_SIGN, model: 'lore' }, { x: 392, y: 203 }));

/** Solid pieces of the points of interest (sim obstacles; renderer kinds in outdoor/layout.ts). */
function poiBlockers(): Blocker[] {
  const out: Blocker[] = [];
  // The Hollowcrown: the trunk (its roots and crown are dressing).
  out.push({ kind: 'eldertree', at: [ELDER_TREE.x, ELDER_TREE.y], r: 4.4 });
  // The Old Beacon: a ruined tower on its hillock.
  out.push({ kind: 'ruin_tower', at: [BEACON.x, BEACON.y], r: 3, facing: SOUTH });
  // The hermit's hut, door to the road.
  out.push({ kind: 'hermit_hut', at: [HERMIT.x, HERMIT.y], r: 2.8, facing: Math.PI, shape: rect(HERMIT, 5.6, 5, Math.PI) });
  // The Ring of Hours: eight rough menhirs (gaps on the trail side).
  for (let i = 0; i < 8; i++) {
    const a = (i / 8) * Math.PI * 2 + 0.55;
    out.push({ kind: 'menhir', at: [r1(STONE_RING.x + Math.cos(a) * 8.5), r1(STONE_RING.y + Math.sin(a) * 8.5)], r: 0.9, facing: r3(a) });
  }
  // The Stone King: a toppled giant statue (plinth with its feet, the torso, the head, a hand).
  const k = STONE_KING;
  out.push({ kind: 'statue_plinth', at: [k.x - 8, k.y + 7], r: 2.4, facing: -0.6 });
  out.push({ kind: 'statue_torso', at: [k.x, k.y], r: 2, facing: -0.35, shape: rect(k, 3.6, 9, -0.35) });
  out.push({ kind: 'statue_head', at: [k.x + 8, k.y - 3.5], r: 2, facing: 0.4 });
  out.push({ kind: 'statue_hand', at: [k.x - 7, k.y - 8], r: 1.5, facing: 2.2 });
  // The angler's camp by the river: a tent.
  out.push({ kind: 'tent', at: [ANGLER.x - 6, ANGLER.y - 4], r: 1.8, facing: 0.3 });
  // The abandoned camp: a torn tent and a broken cart.
  out.push({ kind: 'tent_torn', at: [OLD_CAMP.x + 4, OLD_CAMP.y + 4], r: 1.8, facing: -0.4 });
  out.push({ kind: 'cart_wreck', at: [OLD_CAMP.x - 8, OLD_CAMP.y + 5], r: 1.6, facing: 1.2 });
  // The wayside shrine.
  out.push({ kind: 'wayside_shrine', at: [SHRINE.x, SHRINE.y], r: 1.3, facing: NORTH });
  return out;
}

/** Dressing round the points of interest (walk-through). */
function poiProps(): PropHint[] {
  const out: PropHint[] = [];
  const at = (c: Vec2, dx: number, dy: number): Vec2 => ({ x: r1(c.x + dx), y: r1(c.y + dy) });
  // The Hollowcrown: wildflowers and mushroom rings in its shade, a bench facing the view.
  out.push({ kind: 'wildflowers', pos: at(ELDER_TREE, 14, 6), scale: 7 }, { kind: 'wildflowers', pos: at(ELDER_TREE, -12, -12), scale: 6 }, { kind: 'wildflowers', pos: at(ELDER_TREE, -6, 16), scale: 5 });
  out.push({ kind: 'mushroom_ring', pos: at(ELDER_TREE, -13, 2), scale: 3 }, { kind: 'mushroom_ring', pos: at(ELDER_TREE, 9, -15), scale: 2.5 });
  out.push({ kind: 'bench', pos: at(ELDER_TREE, 9, -10), facing: -0.6 });
  // The Old Beacon: rubble and a cold brazier.
  out.push({ kind: 'campfire_cold', pos: at(BEACON, 5, -5) }, { kind: 'rocks', pos: at(BEACON, -6, 4) }, { kind: 'rocks', pos: at(BEACON, 4, 7) });
  // The hermit's hut: a woodpile, a chopping stump, a garden fence, a lantern and a fire.
  out.push({ kind: 'woodpile', pos: at(HERMIT, 3, -5.5), facing: 0 }, { kind: 'stump', pos: at(HERMIT, -6, -6) }, { kind: 'campfire', pos: at(HERMIT, -7, 7) });
  out.push({ kind: 'fence', pos: at(HERMIT, 2, 9), facing: 0, scale: 1.5 }, { kind: 'fence', pos: at(HERMIT, 7, 6), facing: NORTH, scale: 1 }, { kind: 'lantern', pos: at(HERMIT, -5, -3) });
  out.push({ kind: 'crop_rows', pos: at(HERMIT, 2, 6), scale: 3 }, { kind: 'barrel', pos: at(HERMIT, 4, 3.5) });
  // The Ring of Hours: crystals in the grass at its heart.
  out.push({ kind: 'crystals', pos: at(STONE_RING, 2.5, -2) }, { kind: 'wildflowers', pos: at(STONE_RING, 0, 0), scale: 4 });
  // The Stone King: rubble round the fall.
  out.push({ kind: 'rocks', pos: at(STONE_KING, 4, 5) }, { kind: 'rocks', pos: at(STONE_KING, -3, -6) }, { kind: 'wildflowers', pos: at(STONE_KING, 2, 9), scale: 4 });
  // The angler's camp: a jetty, a rowboat on the bank, a drying rack, a fire, a barrel.
  out.push({ kind: 'dock', pos: at(ANGLER, 9, 1), facing: 0 }, { kind: 'rowboat', pos: at(ANGLER, 10, 7), facing: 1.3 }, { kind: 'drying_rack', pos: at(ANGLER, -1, 6), facing: 0 });
  out.push({ kind: 'campfire', pos: at(ANGLER, -1, -2) }, { kind: 'barrel', pos: at(ANGLER, -9, -6) }, { kind: 'reeds', pos: at(ANGLER, 9, -6), scale: 2 });
  // The abandoned camp: a cold fire, a bedroll, crates.
  out.push({ kind: 'campfire_cold', pos: at(OLD_CAMP, 0, 0) }, { kind: 'bedroll', pos: at(OLD_CAMP, 2, -4), facing: 0.3 }, { kind: 'crates', pos: at(OLD_CAMP, 8, -1) });
  // The wayside shrine: flowers left for the road.
  out.push({ kind: 'wildflowers', pos: at(SHRINE, 0, 2.5), scale: 2.2 });
  // Signposts where the side trails leave the roads.
  for (const t of TRAILS.slice(8)) out.push({ kind: 'signpost', pos: { x: r1(t[0]![0] + 2), y: r1(t[0]![1] + 2) }, facing: r3(Math.atan2(t[1]![1] - t[0]![1], t[1]![0] - t[0]![0])) });
  return out;
}

const PORTALS: PortalDef[] = [{ id: PORTAL.barrowEntrance, name: 'The Hollow Barrow', pos: BARROW_MOUTH, to: MAP.hollowBarrow, kind: 'dungeon', facing: BARROW_FACING, minLevel: 3, radius: 2.2 }];

// ---------------------------------------------------------------------------
// Obstacles: water, cliffs, buildings, ruins, camps, the forest
// ---------------------------------------------------------------------------

/** The deep lake: wobbly shoreline pulled in, open on the west where the sandbar wades to Wisp Isle. */
function lakeObstacle(): Polygon {
  const { c, rx, ry, island, isle, causewayHalf } = LAKE;
  const rd = (a: number): number => {
    const s = lakeShape(a);
    return s.f * LAKE_DEEP - 0.42 * s.bay;
  };
  const ri = island + 1;
  const dOut = Math.asin(Math.min(0.99, causewayHalf / (ry * rd(Math.PI))));
  const dIn = Math.asin(Math.min(0.99, causewayHalf / ri));
  const pts: Vec2[] = [];
  const N = 96;
  for (let k = 0; k <= N; k++) {
    const a = Math.PI + dOut + ((2 * Math.PI - 2 * dOut) * k) / N;
    const r = rd(a);
    pts.push({ x: r1(c.x + Math.cos(a) * rx * r), y: r1(c.y + Math.sin(a) * ry * r) });
  }
  const M = 20;
  for (let k = 0; k <= M; k++) {
    const a = 3 * Math.PI - dIn - ((2 * Math.PI - 2 * dIn) * k) / M;
    pts.push({ x: r1(isle.x + Math.cos(a) * ri), y: r1(isle.y + Math.sin(a) * ri) });
  }
  return { points: pts };
}

/** The river below the falls, split at the ford. */
function riverObstacles(): Polygon[] {
  const below = RIVER.filter((p) => p.y <= FALLS.y + 0.5);
  const pieces: Vec2[][] = [[], []];
  for (const p of below) {
    if (p.y > FORD.y1) pieces[0]!.push(p);
    else if (p.y < FORD.y0) pieces[1]!.push(p);
  }
  const at = (y: number): Vec2 => {
    for (let i = 0; i < below.length - 1; i++) {
      const a = below[i]!;
      const b = below[i + 1]!;
      if ((a.y - y) * (b.y - y) <= 0) {
        const t = (y - a.y) / (b.y - a.y);
        return { x: a.x + (b.x - a.x) * t, y };
      }
    }
    return { x: below[0]!.x, y };
  };
  pieces[0]!.push(at(FORD.y1));
  pieces[1]!.unshift(at(FORD.y0));
  return [...strip(pieces[0]!, RIVER_HALF), ...strip(pieces[1]!, RIVER_HALF)];
}

/**
 * Greyhorn's cliffs: bands along the wandering rims (plateauRimY / plateauRimX) on the south face
 * (open at Goat's Climb, a notch at the falls) and the west face up toward the saddle.
 */
function cliffObstacles(): Polygon[] {
  const out: Polygon[] = [];
  const fx = FALLS.x;
  const band = (xa: number, xb: number): void => {
    const pts: Vec2[] = [];
    for (let x = xa; x < xb; x += 6) pts.push(P(r1(x), r1(plateauRimY(x) - 5.5)));
    pts.push(P(r1(xb), r1(plateauRimY(xb) - 5.5)));
    out.push(...strip(pts, 6));
  };
  // (Each strip piece overhangs its ends by 3.6 yd: stop short so the ramp and the falls stay open.)
  band(plateauRimX(PLATEAU_Y0) - 10, RAMP.x0 - 3.6);
  band(RAMP.x1 + 3.6, fx - 7.6);
  band(fx + 7.6, 880);
  // Over the falls themselves: only the lip (the plunge pool below is river).
  out.push(poly([fx - 4, PLATEAU_Y0 - 3], [fx + 4, PLATEAU_Y0 - 3], [fx + 4, PLATEAU_Y0 + 0.5], [fx - 4, PLATEAU_Y0 + 0.5]));
  // The west face, from the south-west corner up toward the saddle.
  const west: Vec2[] = [];
  for (let y = PLATEAU_Y0 - 4; y < SADDLE_Y - 10; y += 6) west.push(P(r1(plateauRimX(y) - 4.5), r1(y)));
  west.push(P(r1(plateauRimX(SADDLE_Y - 10) - 4.5), SADDLE_Y - 10));
  out.push(...strip(west, 5.5));
  return out;
}

interface Blocker {
  kind: string;
  at: [number, number];
  r: number;
  facing?: Radians;
  shape?: Polygon;
}

const octagon = (c: Vec2, r: number): Polygon => circle(c, r, 8, Math.PI / 8);

/** Town houses around the square (gaps for the four roads), stalls, the well, the forge. */
function townBlockers(): Blocker[] {
  const out: Blocker[] = [];
  const angles = [28, 62, 118, 152, 208, 242, 298, 332];
  angles.forEach((deg, i) => {
    const a = (deg * Math.PI) / 180;
    const r = TOWN_R + 7 + (i % 2) * 2;
    const c = { x: r1(TOWN.x + Math.cos(a) * r), y: r1(TOWN.y + Math.sin(a) * r) };
    const facing = r3(a + Math.PI);
    out.push({ kind: 'cottage', at: [c.x, c.y], r: 3.2, facing, shape: rect(c, 7.2, 6, facing) });
  });
  out.push({ kind: 'well', at: [TOWN.x, TOWN.y], r: 2.2 });
  out.push({ kind: 'forge', at: [TOWN.x - 20, TOWN.y - 9], r: 2.4, facing: 0.4 });
  out.push({ kind: 'market_stall', at: [TOWN.x + 16, TOWN.y + 8], r: 1.8, facing: r3(angleTo(T(16, 8), TOWN)) });
  out.push({ kind: 'market_stall', at: [TOWN.x + 17, TOWN.y - 7], r: 1.8, facing: r3(angleTo(T(17, -7), TOWN)) });
  return out;
}

/** Ruins scattered over the Pale Rise (seeded), clear of its roads, the lookout and the gate. */
function riseRuins(): Blocker[] {
  const out: Blocker[] = [];
  const kinds = ['ruin_wall_pale', 'ruin_pillar', 'ruin_wall_pale', 'standing_stone', 'ruin_pillar'] as const;
  for (let i = 0, t = 0; out.length < 16 && t < 400; t++) {
    const a = hash01(t, 811) * Math.PI * 2;
    const r = 26 + hash01(t, 813) * 92;
    const p = { x: r1(HILL.c.x + Math.cos(a) * r), y: r1(HILL.c.y + Math.sin(a) * r) };
    if (nearRoad(p, 9) || d2(p, LOOKOUT) < 22 || d2(p, TOWER_GATE) < 30 || out.some((b) => d2(P(b.at[0], b.at[1]), p) < 16)) continue;
    const kind = kinds[i++ % kinds.length]!;
    out.push({ kind, at: [p.x, p.y], r: kind === 'ruin_wall_pale' ? 2.6 : 1.0, facing: r3(hash01(t, 817) * Math.PI) });
  }
  return out;
}

/** +spawn: Granny Pell's homestead at the edge of the map: the cottage, a woodshed, a water butt. */
function pellBlockers(): Blocker[] {
  const h = PELL_HOME;
  return [
    { kind: 'cottage', at: [h.x - 8, h.y - 6], r: 3.2, facing: r3(NORTH + 0.15), shape: rect({ x: h.x - 8, y: h.y - 6 }, 7.2, 6, r3(NORTH + 0.15)) },
    { kind: 'woodpile', at: [h.x - 15, h.y + 1], r: 1.1, facing: 0.2 },
    { kind: 'barrel', at: [h.x - 2.5, h.y - 4], r: 0.6 },
  ];
}

const BLOCKERS: Blocker[] = [
  ...townBlockers(),
  ...pellBlockers(),
  // The barrow's rock outcrop behind its mouth, broken columns on the approach.
  { kind: 'barrow_mound', at: [BARROW_MOUND.x, BARROW_MOUND.y], r: 4.5, facing: BARROW_FACING },
  { kind: 'standing_stone', at: [r1(BARROW_MOUTH.x + 7), r1(BARROW_MOUTH.y - 2)], r: 1.0 },
  { kind: 'standing_stone', at: [r1(BARROW_MOUTH.x - 6), r1(BARROW_MOUTH.y + 4)], r: 1.0 },
  // Brookside Farm: the barn, the mill, a hay stack.
  { kind: 'cottage', at: [FARM.x - 6, FARM.y + 6], r: 3.2, facing: SOUTH, shape: rect({ x: FARM.x - 6, y: FARM.y + 6 }, 7.2, 6, SOUTH) },
  { kind: 'windmill', at: [FARM.x + 12, FARM.y + 12], r: 2.6 },
  { kind: 'haystack', at: [FARM.x + 8, FARM.y - 8], r: 1.2 },
  // The Lakeside Outpost: two lodges.
  { kind: 'cottage', at: [OUTPOST.x - 14, OUTPOST.y + 10], r: 3.2, facing: -0.6, shape: rect(O(-14, 10), 7.2, 6, -0.6) },
  { kind: 'cottage', at: [OUTPOST.x + 2, OUTPOST.y + 18], r: 3.2, facing: SOUTH, shape: rect(O(2, 18), 7.2, 6, SOUTH) },
  // The Briar Camp: tents, a lookout and palisades.
  { kind: 'tent', at: [CAMP.x - 10, CAMP.y + 10], r: 2.6, facing: SOUTH },
  { kind: 'tent', at: [CAMP.x + 11, CAMP.y + 9], r: 2.6, facing: SOUTH },
  { kind: 'watchtower', at: [CAMP.x - 16, CAMP.y - 8], r: 2.4 },
  { kind: 'pier', at: [CAMP.x - 18, CAMP.y + 14], r: 1, shape: rect({ x: CAMP.x - 18, y: CAMP.y + 14 }, 12.5, 1.2, 0.6) },
  { kind: 'pier', at: [CAMP.x + 20, CAMP.y + 16], r: 1, shape: rect({ x: CAMP.x + 20, y: CAMP.y + 16 }, 12.5, 1.2, -0.5) },
  // The Pale Rise: broken columns, walls and arch piers among the ruins.
  { kind: 'ruin_pillar', at: [418, 620], r: 1.0 },
  { kind: 'ruin_pillar', at: [466, 628], r: 1.0 },
  { kind: 'ruin_pillar', at: [424, 676], r: 1.0 },
  { kind: 'ruin_pillar', at: [462, 680], r: 1.0 },
  { kind: 'ruin_wall_pale', at: [388, 640], r: 2.6, facing: 0.3 },
  { kind: 'ruin_wall_pale', at: [500, 636], r: 2.6, facing: -0.4 },
  { kind: 'pier', at: [437.4, 600], r: 0.9 },
  { kind: 'pier', at: [442.6, 600], r: 0.9 },
  { kind: 'pier', at: [TOWER_GATE.x - 2.6, TOWER_GATE.y], r: 0.9 },
  { kind: 'pier', at: [TOWER_GATE.x + 2.6, TOWER_GATE.y], r: 0.9 },
  { kind: 'pier', at: [LOOKOUT.x, LOOKOUT.y + 12], r: 1, shape: rect({ x: LOOKOUT.x, y: LOOKOUT.y + 12 }, 11, 2.2, SOUTH) },
  // Tallowdeep: the spoil heap and the timber stack.
  { kind: 'spoil_heap', at: [MINE.x + 2, MINE.y - 22], r: 2.6 },
  { kind: 'mine_beams', at: [MINE.x - 14, MINE.y + 2], r: 1.4, facing: -0.5 },
  // The mine's crags and spoil, ruins out in the quiet country (things to see on the road).
  { kind: 'spoil_heap', at: [MINE.x - 30, MINE.y + 8], r: 2.6 },
  { kind: 'cliff', at: [MINE.x - 4, MINE.y + 36], r: 3 },
  { kind: 'cliff', at: [MINE.x + 40, MINE.y - 4], r: 2.8 },
  { kind: 'ruin_tower', at: [360, 760], r: 2.6, facing: SOUTH },
  { kind: 'ruin_colonnade', at: [540, 260], r: 3, facing: Math.PI },
  { kind: 'ruin_wall_pale', at: [600, 440], r: 2.6, facing: 0.3 },
  { kind: 'standing_stone', at: [360, 420], r: 1.0 },
  { kind: 'standing_stone', at: [502, 520], r: 1.0 },
  // The Pale Rise: more ruins strewn over the hill (broken walls, columns, a toppled arch).
  ...riseRuins(),
  // Boulders.
  { kind: 'boulder', at: [700, 690], r: 2.4 },
  { kind: 'boulder', at: [790, 640], r: 2.2 },
  { kind: 'boulder', at: [360, 210], r: 2 },
  { kind: 'boulder', at: [520, 420], r: 2.2 },
];

/** Rocky ridges inside the zone: they break up the open country (crags stand on their 'cliff' hints). */
const RIDGES: Polygon[] = [
  poly([500, 300], [516, 292], [526, 312], [520, 336], [504, 340], [496, 320]),
  poly([300, 200], [318, 194], [326, 210], [318, 226], [302, 222]),
  poly([546, 470], [562, 462], [574, 480], [568, 504], [552, 506], [542, 488]),
  poly([340, 520], [356, 514], [364, 532], [352, 548], [338, 540]),
  poly([600, 120], [616, 110], [632, 126], [624, 146], [606, 144]),
  poly([190, 410], [210, 402], [222, 420], [208, 438], [192, 432]),
];
const ridgeHints = (): PropHint[] =>
  RIDGES.flatMap((r) => {
    const out: PropHint[] = [];
    const xs = r.points.map((q) => q.x);
    const ys = r.points.map((q) => q.y);
    for (let y = Math.min(...ys) + 2; y < Math.max(...ys); y += 4.5)
      for (let x = Math.min(...xs) + 2; x < Math.max(...xs); x += 4.5) if (pointIn({ x, y }, r)) out.push({ kind: 'cliff', pos: { x: r1(x), y: r1(y) }, scale: 2.6 });
    return out;
  });

// ---------------------------------------------------------------------------
// Spawns: hunting grounds per area, spread into single mobs (open-world spacing)
// ---------------------------------------------------------------------------

type SpawnRow = [id: string, npcs: string[], x: number, y: number, radius: number, count: number, lo: number, hi: number, extra?: Partial<FieldSpawnDef>];
const passive: Partial<FieldSpawnDef> = { aggroRadius: 0 };
const rare = (extra: Partial<FieldSpawnDef> = {}): Partial<FieldSpawnDef> => ({ elite: true, rare: true, respawnMs: RESPAWN.rare, aggroRadius: 10, ...extra });

/**
 * Hunting-ground centres in a disc: dart-thrown (seeded), at least `gap` apart, inside the named
 * area, off the roads, clear of `avoid` spots.
 */
function grounds(c: Vec2, R: number, n: number, salt: number, area: string, gap = 30, avoid: readonly { p: Vec2; r: number }[] = []): Vec2[] {
  const out: Vec2[] = [];
  for (let t = 0; t < 900 && out.length < n; t++) {
    const a = hash01(t, salt) * Math.PI * 2;
    const rr = R * Math.sqrt(hash01(t, salt + 1));
    const p = { x: r1(c.x + Math.cos(a) * rr), y: r1(c.y + Math.sin(a) * rr) };
    if (!pointIn(p, BOUNDARY)) continue;
    if (areaOf(p) !== area && regionOf(p) !== area) continue;
    if (nearRoad(p, 12) || out.some((q) => d2(q, p) < gap) || avoid.some((v) => d2(v.p, p) < v.r)) continue;
    out.push(p);
  }
  return out;
}

const rowsAt = (prefix: string, pts: readonly Vec2[], npcs: (i: number) => string[], count: number, lv: (i: number) => [number, number], extra: (i: number) => Partial<FieldSpawnDef> = () => ({})): SpawnRow[] =>
  pts.map((p, i) => [`${prefix}_${i}`, npcs(i), p.x, p.y, 6, count, lv(i)[0], lv(i)[1], extra(i)]);

const keepBarrow = [{ p: BARROW_MOUTH, r: 30 }];
const keepLookout = [{ p: LOOKOUT, r: 24 }, { p: TOWER_GATE, r: 34 }, { p: { x: 418, y: 548 }, r: 20 }];

const SPAWN_ROWS: SpawnRow[] = [
  // ① The Slime Hollows (1–2): slimes only. Meadow Slimes are passive; a few Bloom Slime dells bite back.
  ...rowsAt('sh', grounds(SLIME, 78, 11, 101, 'slime_hollows', 28), (i) => (i % 4 === 3 ? [MOB.bloomSlime] : [MOB.meadowSlime]), 4, (i) => (i % 4 === 3 ? [2, 2] : [1, 2]), (i) => (i % 4 === 3 ? {} : passive)),
  // ② The Boar Run and Brookside Farm (2–3): boars in the barley, bloom slimes in the gardens.
  ...rowsAt('br', grounds(BOAR_RUN, 74, 10, 211, 'boar_run', 28, [{ p: FARM, r: 26 }]), () => [MOB.tuskBoar], 4, (i) => (i % 3 === 0 ? [2, 3] : [3, 3])),
  ...rowsAt('bf_farm', grounds(FARM, 46, 2, 223, 'sunmeadow', 26, [{ p: FARM, r: 16 }]), () => [MOB.bloomSlime], 4, () => [2, 3]),
  ['sm_rare_mossback', [MOB.oldMossback], 56, 392, 4, 1, 4, 4, rare()],
  // ③ Barrowfield (3–4): the last boars and blooms before the Barrow.
  ...rowsAt('bfd', grounds(BARROWFIELD, 66, 5, 307, 'barrowfield', 30, keepBarrow), (i) => (i % 2 ? [MOB.bloomSlime] : [MOB.tuskBoar]), 4, () => [3, 4]),
  // ⑤ Glimmermere (4–5): croakers and snappers round the shore, snappers on Wisp Isle.
  ...rowsAt('gl', lakeShoreGrounds(), (i) => (i % 2 ? [MOB.mireCroaker] : [MOB.reedbackSnapper]), 4, (i) => (i % 3 === 0 ? [4, 4] : [4, 5])),
  ['gl_isle', [MOB.reedbackSnapper], LAKE.isle.x - 2, LAKE.isle.y - 3, 4, 4, 5, 5],
  ['gl_rare_snapjaw', [MOB.oldSnapjaw], LAKE.isle.x + 5, LAKE.isle.y + 4, 2, 1, 6, 6, rare()],
  // ⑥ Greyhorn Heights (5–7): hounds on the southern plateau and the eastern foothills, the Tallow folk by the mine.
  // Hounds roam the plateau's southern half (5–6); the Tallow folk work the ground toward the mine (6–7).
  ...(() => {
    const pts = grounds(HEIGHTS, 92, 12, 401, 'greyhorn', 30, [{ p: { x: 654, y: 548 }, r: 24 }, { p: MINE, r: 40 }]);
    const miners = (p: Vec2): boolean => d2(p, MINE) < 105;
    return pts.map((p, i): SpawnRow => [`gh_${i}`, miners(p) ? (i % 2 ? [MOB.tallowDigger, MOB.tallowChanter] : [MOB.tallowDigger]) : [MOB.cragHound], p.x, p.y, 6, 4, miners(p) ? 6 : 5, miners(p) ? 7 : 6]);
  })(),
  ...rowsAt('ghf', grounds({ x: 780, y: 450 }, 50, 3, 433, 'greyhorn', 30), () => [MOB.cragHound], 4, () => [5, 5]),
  ['gh_camp_a', [MOB.tallowDigger, MOB.tallowChanter], MINE.x - 26, MINE.y - 18, 5, 4, 6, 7],
  ['gh_camp_b', [MOB.tallowChanter, MOB.tallowDigger], MINE.x - 8, MINE.y - 36, 5, 3, 6, 7],
  ['gh_rare_foreman', [MOB.foremanTallowick], MINE.x - 10, MINE.y - 6, 2, 1, 7, 7, rare()],
  // ⑦ Bramblewood (7–9): wolves at the forest edge, bandits deeper, the Thornheart and the Briar Camp.
  ...rowsAt('bwe', grounds(BRAMBLE_EDGE, 70, 8, 503, 'bramblewood', 28, [{ p: { x: 264, y: 494 }, r: 20 }]), () => [MOB.brambleWolf], 4, () => [7, 7]),
  ...rowsAt('bwd', grounds(BRAMBLE_DEEP, 66, 6, 521, 'bramblewood', 30), (i) => (i % 2 ? [MOB.briarBandit] : [MOB.brambleWolf]), 4, () => [7, 8]),
  ...rowsAt('bwt', grounds({ x: 90, y: 690 }, 60, 5, 541, 'thornheart', 28, [{ p: CAMP, r: 34 }]), (i) => (i % 2 ? [MOB.banditSlinger, MOB.briarBandit] : [MOB.briarBandit]), 4, () => [8, 9]),
  ['bw_camp_a', [MOB.briarBandit, MOB.banditSlinger], CAMP.x - 18, CAMP.y - 18, 6, 3, 8, 9],
  ['bw_camp_b', [MOB.banditSlinger, MOB.briarBandit], CAMP.x + 22, CAMP.y - 16, 6, 3, 8, 9],
  ['bw_captain', [MOB.banditCaptain], CAMP.x, CAMP.y + 2, 3, 1, 9, 10, { elite: true, respawnMs: RESPAWN.elite, aggroRadius: 10 }],
  ['bw_rare_greymaw', [MOB.greymaw], 60, 560, 3, 1, 8, 8, rare()],
  // ⑧ The Pale Rise (9–10): sentinels and shades among the ruins.
  ...rowsAt('pr', grounds(HILL.c, 112, 12, 601, 'pale_rise', 30, keepLookout), (i) => (i % 2 ? [MOB.hollowShade] : [MOB.paleSentinel]), 4, (i) => (i % 3 === 0 ? [9, 9] : [9, 10])),
  ['pr_rare_warden', [MOB.paleWarden], 498, 700, 3, 1, 10, 10, rare()],
  // Patrols: footpads on the west road (red to newcomers), hounds over the ford, a sentinel on the Rise road.
  ['pt_west_road', [MOB.briarBandit], 272, 488, 3, 2, 7, 7, { movement: { type: 'patrol', points: [P(292, 476), P(272, 488), P(236, 520), P(214, 556)], mode: 'pingpong', pauseMs: [2000, 5000] } }],
  ['pt_ford', [MOB.cragHound], 760, 422, 3, 2, 5, 5, { movement: { type: 'patrol', points: [P(722, 412), P(760, 422), P(800, 434)], mode: 'pingpong', pauseMs: [1500, 4000] } }],
  ['pt_rise', [MOB.paleSentinel], 438, 598, 2, 1, 9, 9, { movement: { type: 'patrol', points: [P(432, 560), P(438, 598), P(440, 640)], mode: 'pingpong', pauseMs: [3000, 6000] } }],
];

/** Grounds round Glimmermere's shore (not at the outpost). */
function lakeShoreGrounds(): Vec2[] {
  const out: Vec2[] = [];
  for (let i = 0; i < 16; i++) {
    const a = (i / 16) * Math.PI * 2 + 0.15;
    const f = lakeShape(a).f * (1.18 + hash01(i, 77) * 0.12);
    const p = { x: r1(LAKE.c.x + Math.cos(a) * LAKE.rx * f), y: r1(LAKE.c.y + Math.sin(a) * LAKE.ry * f) };
    if (d2(p, OUTPOST) < 46 || !pointIn(p, BOUNDARY) || nearRoad(p, 10) || distToPolyline(p, RIVER) < 14) continue;
    if (out.some((q) => d2(q, p) < 28)) continue;
    out.push(p);
  }
  return out;
}

/**
 * Open-world spacing (docs/design/zones.md "Spacing"): grouping is for dungeons. Each row above is
 * a hunting ground, spread into single mobs ~10–14 yd apart (seeded, dart-thrown, deterministic) so
 * you pull one at a time; a couple of rows keep a pair; the camps stand at loose posts (~7 yd)
 * instead of in a ball; elites, rares and patrols stay as authored.
 */
const CAMPS: ReadonlySet<string> = new Set(['gh_camp_a', 'gh_camp_b', 'bw_camp_a', 'bw_camp_b']);
const PAIRS: ReadonlySet<string> = new Set(['bwe_1', 'bwe_4', 'bwd_1']);

function spreadSpawns(): FieldSpawnDef[] {
  const blocked: Polygon[] = [lakeObstacle(), ...riverObstacles(), ...cliffObstacles(), ...RIDGES, ...BLOCKERS.map((b) => b.shape ?? octagon(P(b.at[0], b.at[1]), b.r))];
  const ring = (c: Vec2, r: number): Vec2[] => [c, ...Array.from({ length: 8 }, (_, i) => ({ x: c.x + Math.cos((i * Math.PI) / 4) * r, y: c.y + Math.sin((i * Math.PI) / 4) * r }))];
  const open = (c: Vec2, pad: number): boolean => ring(c, pad).every((q) => pointIn(q, BOUNDARY) && !blocked.some((o) => pointIn(q, o)));
  const mouthOut = { x: BARROW_MOUTH.x + Math.cos(BARROW_FACING) * 5.2, y: BARROW_MOUTH.y + Math.sin(BARROW_FACING) * 5.2 };
  const keepClear: Vec2[] = [ZONE_START, BARROW_MOUTH, mouthOut, ...WAYSTONES.map((w) => w.pos)];
  const safe = AREAS.filter((a) => a.safe);
  const placed: Vec2[] = [];
  const out: FieldSpawnDef[] = [];
  SPAWN_ROWS.forEach(([id, npcIds, x, y, radius, count, lo, hi, extra], gi) => {
    const base = { npcIds, level: [lo, hi] as [number, number], respawnMs: RESPAWN.normal, ...(extra ?? {}) };
    if (count <= 1 || extra?.movement?.type === 'patrol') {
      out.push({ id, pos: { x, y }, radius, count, ...base });
      placed.push({ x, y });
      return;
    }
    const camp = CAMPS.has(id);
    const units: number[] = PAIRS.has(id) ? [2, ...new Array<number>(Math.max(0, count - 2)).fill(1)] : new Array<number>(count).fill(1);
    const passiveRow = extra?.aggroRadius === 0;
    const spacing = camp ? 7 : passiveRow ? 10 : 12.5;
    const R = spacing * (0.55 + 0.6 * Math.sqrt(units.length));
    const home = regionOf({ x, y });
    const mine: Vec2[] = [];
    let minD = spacing;
    for (let pass = 0; pass < 5 && mine.length < units.length; pass++, minD *= 0.85)
      for (let t = 0; t < 400 && mine.length < units.length; t++) {
        const a = hash01(t + pass * 997, gi * 13 + 1) * Math.PI * 2;
        const rr = R * Math.sqrt(hash01(t + pass * 997, gi * 13 + 2));
        const p = { x: r1(x + Math.cos(a) * rr), y: r1(y + Math.sin(a) * rr) };
        if (!open(p, 3.5)) continue;
        if (mine.some((q) => d2(q, p) < minD) || placed.some((q) => d2(q, p) < Math.min(minD, 8))) continue;
        if (safe.some((s) => pointIn(p, s.shape)) || d2(p, TOWN) < TOWN_R + 12) continue;
        if (!passiveRow && keepClear.some((k) => d2(k, p) < 16)) continue;
        if (nearRoad(p, 2.5)) continue;
        if (regionOf(p) !== home) continue;
        mine.push(p);
      }
    units.forEach((n, k) => {
      const pos = mine[k] ?? { x, y };
      placed.push(pos);
      out.push({ id: `${id}.${k}`, pos, radius: n === 2 ? 3 : camp ? 1.5 : 3.5, count: n, ...base, ...(camp ? { movement: { type: 'idle' } } : {}) });
    });
  });
  return out;
}

/** +specs: Warden Ilsa's practice dummy (Advancement Trial), a few steps from her on the Tower Road. */
export const PRACTICE_DUMMY_POS: Vec2 = { x: TOWER_GATE.x + 4, y: TOWER_GATE.y - 12 };
const SPAWNS: FieldSpawnDef[] = [
  ...spreadSpawns(),
  // Radius 0 and a fixed facing: the dummy draws no randomness, so the zone's seeded spawns are unchanged.
  { id: 'tower_practice_dummy', npcIds: [MOB.practiceDummy], pos: PRACTICE_DUMMY_POS, radius: 0, facing: Math.PI * 0.75, count: 1, level: [10, 10], aggroRadius: 0, movement: { type: 'idle' }, respawnMs: 5000 },
];

/**
 * +detail: ruins along the roads in the quiet country (broken walls, toppled statues, menhirs,
 * columns), about one every 60 yd of road, set back from the verge; never in an area, near a spawn,
 * an NPC, a point of interest, the water or another blocker.
 */
function roadsideRuins(): Blocker[] {
  const out: Blocker[] = [];
  const kinds = ['ruin_wall_pale', 'statue_broken', 'menhir', 'ruin_pillar', 'ruin_wall_pale', 'menhir'] as const;
  const radius: Record<(typeof kinds)[number], number> = { ruin_wall_pale: 2.2, statue_broken: 1.3, menhir: 0.9, ruin_pillar: 1.0 };
  const others: { pos: Vec2; r: number }[] = [
    ...BLOCKERS.map((b) => ({ pos: P(b.at[0], b.at[1]), r: b.r + 6 })),
    ...POIS.map((p) => ({ pos: p.pos, r: p.r + 6 })),
    ...SPAWNS.map((sp) => ({ pos: sp.pos, r: sp.radius + 14 })),
    ...NPCS.map((n) => ({ pos: n.pos, r: 8 })),
    ...SECRETS.map((sc) => ({ pos: sc.pos, r: 8 })),
  ];
  let n = 0;
  ROAD_LINES.forEach((line, li) => {
    let next = 30 + hash01(li, 901) * 30;
    let run = 0;
    for (let i = 1; i < line.length; i++) {
      const a = line[i - 1]!;
      const b = line[i]!;
      const len = d2(a, b);
      if (len < 0.1) continue;
      for (let t = 0; t < len; t += 2) {
        run += 2;
        if (run < next) continue;
        const side = hash01(n, 907) < 0.5 ? -1 : 1;
        const off = 9 + hash01(n, 911) * 4;
        const ux = (b.x - a.x) / len;
        const uy = (b.y - a.y) / len;
        const p = { x: r1(a.x + ux * t - uy * off * side), y: r1(a.y + uy * t + ux * off * side) };
        n++;
        if (!pointIn(p, BOUNDARY) || areaOf(p) !== null || d2(p, TOWN) < TOWN_R + 30 || nearRoad(p, 6.5)) continue;
        if (lakeE(p.x, p.y) < 1.7 || distToPolyline(p, RIVER) < 14 || p.y > PLATEAU_Y0 - 30) continue;
        const kind = kinds[n % kinds.length]!;
        const r = radius[kind];
        if (others.some((o) => d2(o.pos, p) < o.r + r) || out.some((o) => d2(P(o.at[0], o.at[1]), p) < 20)) continue;
        out.push({ kind, at: [p.x, p.y], r, facing: r3(Math.atan2(uy, ux) + (kind === 'statue_broken' ? hash01(n, 913) * 6 : 0)) });
        run = 0;
        next = 50 + hash01(n, 919) * 25;
      }
    }
  });
  return out;
}

/** +detail: Brookside Farm's second windmill and the Boar Run's barn (kept clear of the hunting grounds). */
function farmBlockers(): Blocker[] {
  const want: Blocker[] = [
    { kind: 'windmill', at: [118, 226], r: 2.6 },
    { kind: 'windmill', at: [52, 286], r: 2.6 },
  ];
  return want.filter((b) => SPAWNS.every((sp) => d2(sp.pos, P(b.at[0], b.at[1])) > sp.radius + b.r + 8) && !nearRoad(P(b.at[0], b.at[1]), 6));
}

const POI_BLOCKERS: Blocker[] = [...poiBlockers(), ...farmBlockers()];
const ROADSIDE: Blocker[] = roadsideRuins();

/**
 * The forest: tree clumps across Bramblewood (denser toward the Thornheart) and copses in the open
 * country, clear of the roads, spawns, camps, NPCs and landmarks.
 */
function forestClumps(): Blocker[] {
  const out: Blocker[] = [];
  const keep: { pos: Vec2; r: number }[] = [
    ...SPAWNS.map((s) => ({ pos: s.pos, r: s.radius + 4 })),
    ...BLOCKERS.map((b) => ({ pos: P(b.at[0], b.at[1]), r: b.r + 4 })),
    { pos: CAMP, r: 22 },
    { pos: TOWN, r: TOWN_R + 18 },
    { pos: OUTPOST, r: 30 },
    ...NPCS.map((n) => ({ pos: n.pos, r: 5 })),
    ...SECRETS.map((s) => ({ pos: s.pos, r: 5 })),
    ...SKY.keep, // +glide
    ...WAYSTONES.map((w) => ({ pos: w.pos, r: 7 })),
    ...RIDGES.map((r) => ({ pos: { x: r.points.reduce((a, q) => a + q.x, 0) / r.points.length, y: r.points.reduce((a, q) => a + q.y, 0) / r.points.length }, r: 16 })),
    // +detail: the points of interest and the roadside ruins stay in the open.
    ...POIS.map((p) => ({ pos: p.pos, r: p.r })),
    ...POI_BLOCKERS.map((b) => ({ pos: P(b.at[0], b.at[1]), r: b.r + 4 })),
    ...ROADSIDE.map((b) => ({ pos: P(b.at[0], b.at[1]), r: b.r + 4 })),
    ...OUTCROPS.map((o) => ({ pos: o, r: 16 })), // +ore: the rocky knolls stay open
  ];
  const clear = (p: Vec2, roadGap: number): boolean =>
    pointIn(p, BOUNDARY) && !nearRoad(p, roadGap) && !keep.some((k) => d2(k.pos, p) < k.r) && !BOUNDARY.points.some((q) => d2(q, p) < 10);
  let n = 0;
  // Bramblewood.
  for (let y = 430; y < 800; y += 8)
    for (let x = 10; x < 340; x += 8) {
      n++;
      const p = { x: r1(x + (hash01(n, 3) - 0.5) * 6), y: r1(y + (hash01(n, 5) - 0.5) * 6) };
      if (regionOf(p) !== 'bramblewood') continue;
      const deep = Math.min(1, Math.max(0, (p.y - 470) / 260 + (260 - p.x) / 300));
      if (hash01(n, 7) > 0.34 + 0.42 * deep) continue;
      if (!clear(p, 6)) continue;
      out.push({ kind: hash01(n, 11) < 0.25 ? 'tree' : 'tree_clump', at: [p.x, p.y], r: r1(1.4 + hash01(n, 13) * (1.4 + 1.4 * deep)) });
    }
  // Copses in the open country (and woods on the Rise's flanks, the lake's back shore).
  for (let y = 20; y < 800; y += 13)
    for (let x = 20; x < 850; x += 13) {
      n++;
      const p = { x: r1(x + (hash01(n, 3) - 0.5) * 9), y: r1(y + (hash01(n, 5) - 0.5) * 9) };
      const reg = regionOf(p);
      if (reg === 'bramblewood') continue;
      // Copses thicker in the buffer country and round the rim of the Slime Hollows' dells.
      const shRim = Math.abs(d2(p, SLIME) - 80) < 14;
      const chance = reg === null ? 0.16 : shRim ? 0.3 : reg === 'greyhorn' ? 0.05 : 0.08;
      if (hash01(n, 7) > chance) continue;
      if (lakeE(p.x, p.y) < 1.5 || distToPolyline(p, RIVER) < 10 || (p.x > PLATEAU_X0 - 16 && p.y > PLATEAU_Y0 - 16 && p.y < PLATEAU_Y0 + 6)) continue;
      if (!clear(p, 8)) continue;
      out.push({ kind: 'tree_clump', at: [p.x, p.y], r: r1(1.8 + hash01(n, 13) * 1.8) });
    }
  return out;
}

/**
 * +ore: boulder clusters on the rocky knolls (OUTCROPS): three or four boulders each, clear of the
 * spawn points, roads, NPCs and water. The mining nodes sit at their feet.
 */
function outcropBlockers(): Blocker[] {
  const out: Blocker[] = [];
  OUTCROPS.forEach((c, oi) => {
    const n = 3 + (oi % 2);
    for (let i = 0; i < n; i++) {
      const a = (i / n) * Math.PI * 2 + hash01(oi, 71) * 3;
      const d = i === 0 ? 0 : 3.2 + hash01(oi * 7 + i, 73) * 2.2;
      const p = P(r1(c.x + Math.cos(a) * d), r1(c.y + Math.sin(a) * d));
      const r = r1(i === 0 ? 2.3 : 1.3 + hash01(oi * 5 + i, 79) * 0.8);
      if (!pointIn(p, BOUNDARY) || nearRoad(p, r + 3) || lakeE(p.x, p.y) < 1.4 || distToPolyline(p, RIVER) < 9) continue;
      if (SPAWNS.some((sp) => d2(sp.pos, p) < sp.radius + r + 3) || NPCS.some((q) => d2(q.pos, p) < r + 6) || WAYSTONES.some((q) => d2(q.pos, p) < r + 7) || SECRETS.some((q) => d2(q.pos, p) < r + 4)) continue;
      out.push({ kind: 'boulder', at: [p.x, p.y], r });
    }
  });
  return out;
}
const OUTCROP_BLOCKERS: Blocker[] = outcropBlockers();

const ALL_BLOCKERS: Blocker[] = [...BLOCKERS, ...POI_BLOCKERS, ...ROADSIDE, ...OUTCROP_BLOCKERS, ...forestClumps()];

/** +ore: rocky features (centre, radius; ruins apart) where ore may sit: boulders, crags, cliffs, ridges, spoil, ruins. */
export interface RockFeature {
  pos: Vec2;
  r: number;
  ruin: boolean;
}
const ROCKY = /boulder|cliff|spoil|rock|crag/;
const RUINS = /ruin|standing_stone|menhir|statue|pillar/;

/** Cliff dressing: rock hints every few yards along the cliff bands (the renderer stands cliffs on them). */
function cliffHints(cliffs: readonly Polygon[]): PropHint[] {
  // One crag per band piece (quads along the wandering rims), at its middle; the falls lip stays bare.
  const out: PropHint[] = [];
  for (const c of cliffs) {
    const ys = c.points.map((p) => p.y);
    const xs = c.points.map((p) => p.x);
    if (Math.max(...ys) - Math.min(...ys) < 4) continue;
    const cx = xs.reduce((a, v) => a + v, 0) / xs.length;
    const cy = ys.reduce((a, v) => a + v, 0) / ys.length;
    out.push({ kind: 'cliff', pos: { x: r1(cx), y: r1(cy) }, scale: 3 });
  }
  return out;
}

// ---------------------------------------------------------------------------
// Props (renderer dressing)
// ---------------------------------------------------------------------------

function townProps(): PropHint[] {
  const out: PropHint[] = [];
  for (let i = 0; i < 8; i++) {
    const a = (i / 8) * Math.PI * 2 + Math.PI / 8;
    out.push({ kind: 'lamp_post', pos: { x: r1(TOWN.x + Math.cos(a) * 15), y: r1(TOWN.y + Math.sin(a) * 15) } });
  }
  for (const [a, x, y] of [
    [0, 1, 0],
    [NORTH, 0, 1],
    [Math.PI, -1, 0],
    [SOUTH, 0, -1],
  ] as const) {
    for (const s of [-1, 1]) {
      const gx = TOWN.x + x * (TOWN_R + 3) - y * s * 4.5;
      const gy = TOWN.y + y * (TOWN_R + 3) + x * s * 4.5;
      out.push({ kind: 'banner', pos: { x: r1(gx), y: r1(gy) }, facing: r3(a) });
    }
  }
  out.push({ kind: 'bench', pos: T(0, -7), facing: NORTH }, { kind: 'bench', pos: T(0, 7), facing: SOUTH });
  out.push({ kind: 'barrel', pos: T(19, 12) }, { kind: 'crate', pos: T(20, -10) }, { kind: 'barrel', pos: T(-23, -6) });
  out.push({ kind: 'flower_bed', pos: T(-12, 12), scale: 2.5 }, { kind: 'flower_bed', pos: T(12, -12), scale: 2.5 });
  out.push({ kind: 'anvil', pos: T(-18, -4), facing: 0.4 });
  return out;
}

/** +spawn: Granny Pell's garden (walk-through): vegetable rows, a fence, flowers, a washing line, a bench facing the view. */
function pellProps(): PropHint[] {
  const h = PELL_HOME;
  const at = (dx: number, dy: number): Vec2 => ({ x: r1(h.x + dx), y: r1(h.y + dy) });
  return [
    { kind: 'crop_rows', pos: at(-14, 9), scale: 3 },
    { kind: 'crop_rows', pos: at(-7, 12), scale: 2.5 },
    { kind: 'fence', pos: at(-11, 16), facing: 0, scale: 2.5 },
    { kind: 'fence', pos: at(-20, 9), facing: NORTH, scale: 2 },
    { kind: 'wildflowers', pos: at(-3, 3), scale: 3 },
    { kind: 'wildflowers', pos: at(6, -3), scale: 2.5 },
    { kind: 'flower_patch', pos: at(-16, -2), scale: 3 },
    { kind: 'washing_line', pos: at(5, 2), facing: r3(0.25) },
    { kind: 'bench', pos: at(-2, 6), facing: r3(angleTo(at(-2, 6), TOWER_FAR)) },
    { kind: 'lantern', pos: at(-3, -1) },
    { kind: 'scarecrow', pos: at(-12, 14), facing: NORTH },
  ];
}

/** +ore: scree and small rocks round the outcrops' boulders. */
function outcropProps(): PropHint[] {
  return OUTCROPS.flatMap((c, i): PropHint[] => [
    { kind: 'rocks', pos: { x: r1(c.x + 4 * Math.cos(i * 2.1)), y: r1(c.y + 4 * Math.sin(i * 2.1)) } },
    { kind: 'rocks', pos: { x: r1(c.x - 5 * Math.cos(i * 1.3)), y: r1(c.y - 5 * Math.sin(i * 1.3)) } },
  ]);
}

const PROPS: PropHint[] = [
  ...pellProps(),
  ...outcropProps(),
  ...ALL_BLOCKERS.map((b): PropHint => ({ kind: b.kind, pos: { x: b.at[0], y: b.at[1] }, scale: b.r, ...(b.facing !== undefined ? { facing: b.facing } : {}) })),
  ...cliffHints(cliffObstacles()),
  ...ridgeHints(),
  // Water obstacles take no tree fill (the renderer draws the water).
  { kind: 'water', pos: { x: LAKE.c.x, y: LAKE.c.y - 30 } },
  ...riverObstacles().map((o): PropHint => ({ kind: 'water', pos: { x: r1(o.points.reduce((a, q) => a + q.x, 0) / 4), y: r1(o.points.reduce((a, q) => a + q.y, 0) / 4) } })),
  ...townProps(),
  ...roadHints(),
  { kind: 'barrow_arch', pos: BARROW_MOUTH, facing: BARROW_FACING, scale: 1.2 },
  // Signposts at the forks.
  { kind: 'signpost', pos: T(4, 28), facing: NORTH },
  { kind: 'signpost', pos: T(28, -4), facing: 0 },
  { kind: 'signpost', pos: T(-28, 4), facing: Math.PI },
  { kind: 'signpost', pos: { x: 426, y: 426 }, facing: NORTH },
  { kind: 'signpost', pos: { x: 628, y: 404 }, facing: NORTH },
  { kind: 'signpost', pos: { x: 208, y: 566 }, facing: NORTH },
  { kind: 'signpost', pos: { x: 168, y: 278 }, facing: NORTH },
  // The Slime Hollows: mushroom rings and flower beds in the dells; the Boar Run: hay, fences, carts.
  ...Array.from({ length: 9 }, (_, i): PropHint => {
    const a = i * 2.4 + 0.5;
    const r = 18 + hash01(i, 61) * 55;
    return { kind: i % 3 === 0 ? 'mushroom_ring' : 'flower_patch', pos: { x: r1(SLIME.x + Math.cos(a) * r), y: r1(SLIME.y + Math.sin(a) * r) }, scale: i % 3 === 0 ? 3 + (i % 2) : 5 + (i % 3) };
  }),
  ...Array.from({ length: 7 }, (_, i): PropHint => {
    const a = i * 2.1 + 0.9;
    const r = 22 + hash01(i, 67) * 50;
    return { kind: i % 2 ? 'hay_bale' : 'fence', pos: { x: r1(BOAR_RUN.x + Math.cos(a) * r), y: r1(BOAR_RUN.y + Math.sin(a) * r) }, facing: r3(a), ...(i % 2 ? {} : { scale: 2.5 }) };
  }),
  { kind: 'cart', pos: { x: BOAR_RUN.x + 26, y: BOAR_RUN.y - 6 }, facing: 1.1 },
  // The Slime Hollows and the farm.
  { kind: 'flower_patch', pos: { x: SLIME.x - 30, y: SLIME.y + 10 }, scale: 8 },
  { kind: 'flower_patch', pos: { x: SLIME.x + 34, y: SLIME.y - 20 }, scale: 7 },
  { kind: 'flower_patch', pos: { x: FARM.x - 20, y: FARM.y - 20 }, scale: 6 },
  { kind: 'mushroom_ring', pos: { x: SLIME.x + 8, y: SLIME.y + 40 }, scale: 4 },
  { kind: 'fence', pos: { x: FARM.x - 2, y: FARM.y - 14 }, facing: 0, scale: 3 },
  { kind: 'fence', pos: { x: FARM.x + 18, y: FARM.y - 2 }, facing: NORTH, scale: 2.5 },
  { kind: 'fence', pos: { x: BOAR_RUN.x + 30, y: BOAR_RUN.y + 40 }, facing: 0.4, scale: 3 },
  { kind: 'hay_bale', pos: { x: FARM.x + 3, y: FARM.y - 9 } },
  { kind: 'hay_bale', pos: { x: BOAR_RUN.x + 20, y: BOAR_RUN.y - 30 } },
  { kind: 'cart', pos: { x: FARM.x - 14, y: FARM.y - 2 }, facing: 0.5 },
  { kind: 'log', pos: { x: 300, y: 330 }, facing: 0.6 },
  { kind: 'log', pos: { x: 560, y: 160 }, facing: -0.3 },
  // The outpost and Glimmermere: the dock, reeds along the shores.
  { kind: 'dock', pos: O(14, -18), facing: r3(angleTo(O(14, -18), LAKE.c)) },
  { kind: 'lamp_post', pos: O(-8, -6) },
  { kind: 'lamp_post', pos: O(10, 8) },
  { kind: 'crates', pos: O(-6, 14) },
  { kind: 'barrel', pos: O(6, -2) },
  { kind: 'campfire', pos: O(-2, -4), scale: 1 },
  ...Array.from({ length: 34 }, (_, i): PropHint => {
    const a = (i / 34) * Math.PI * 2 + 0.2;
    const e = (0.97 + hash01(i, 21) * 0.1) * lakeShape(a).f;
    return { kind: 'reeds', pos: { x: r1(LAKE.c.x + Math.cos(a) * LAKE.rx * e), y: r1(LAKE.c.y + Math.sin(a) * LAKE.ry * e) }, scale: r1(1 + hash01(i, 23) * 0.6) };
  }),
  // Greyhorn: the mine mouth, its yard, the falls, crystals in the grotto, crags.
  { kind: 'mine_mouth', pos: { x: MINE.x + 10, y: MINE.y + 18 }, facing: r3(angleTo({ x: MINE.x + 10, y: MINE.y + 18 }, MINE)) },
  { kind: 'lamp_post', pos: { x: MINE.x + 4, y: MINE.y + 10 } },
  { kind: 'lamp_post', pos: { x: MINE.x - 8, y: MINE.y - 4 } },
  { kind: 'crates', pos: { x: MINE.x - 14, y: MINE.y - 8 } },
  { kind: 'ore_cart', pos: { x: MINE.x + 2, y: MINE.y + 4 }, facing: 0.84 },
  { kind: 'ore_cart', pos: { x: MINE.x - 20, y: MINE.y - 12 }, facing: -0.6 },
  { kind: 'mine_rails', pos: { x: MINE.x - 2, y: MINE.y }, facing: 0.84, scale: 1 },
  { kind: 'mine_rails', pos: { x: MINE.x - 12, y: MINE.y - 9 }, facing: 0.84, scale: 1 },
  { kind: 'campfire', pos: { x: MINE.x - 18, y: MINE.y - 24 }, scale: 1 },
  { kind: 'cliff', pos: { x: MINE.x + 26, y: MINE.y + 14 }, scale: 3 },
  { kind: 'cliff', pos: { x: MINE.x + 22, y: MINE.y + 34 }, scale: 3.2 },
  { kind: 'waterfall', pos: { x: FALLS.x, y: FALLS.y }, facing: SOUTH },
  { kind: 'crystals', pos: { x: GROTTO.x + 6, y: GROTTO.y + 4 } },
  { kind: 'crystals', pos: { x: GROTTO.x + 5, y: GROTTO.y - 5 } },
  { kind: 'cave_mouth', pos: { x: GROTTO.x - 6, y: GROTTO.y }, facing: Math.PI },
  // The Briar Camp.
  { kind: 'campfire', pos: { x: CAMP.x, y: CAMP.y - 3 }, scale: 1.2 },
  { kind: 'bandit_banner', pos: { x: CAMP.x + 2, y: CAMP.y + 16 }, facing: SOUTH },
  { kind: 'crate', pos: { x: CAMP.x - 6, y: CAMP.y + 5 } },
  { kind: 'barrel', pos: { x: CAMP.x + 7, y: CAMP.y + 4 } },
  { kind: 'mushroom_ring', pos: { x: 110, y: 640 }, scale: 4 },
  { kind: 'bramble', pos: { x: 190, y: 640 } },
  { kind: 'bramble', pos: { x: 120, y: 670 } },
  // The Pale Rise: ruins on the slopes, a colonnade at the lookout, the Tower gate.
  { kind: 'ruin_arch', pos: { x: 440, y: 600 }, facing: SOUTH },
  { kind: 'ruin_colonnade', pos: { x: LOOKOUT.x, y: LOOKOUT.y + 12 }, facing: SOUTH },
  { kind: 'ruin_arch', pos: { x: 380, y: 660 }, facing: 0 },
  { kind: 'ruin_arch', pos: TOWER_GATE, facing: SOUTH },
  { kind: 'crystals', pos: { x: LOOKOUT.x - 5, y: LOOKOUT.y - 4 } },
  { kind: 'signpost', pos: { x: TOWER_GATE.x - 8, y: TOWER_GATE.y - 14 }, facing: NORTH },
  // Ruins out in the quiet country between the areas (things to see on the road).
  { kind: 'ruin_arch', pos: { x: 340, y: 300 }, facing: 0.5 },
  // Beyond the boundary: ruins on the high ground, a tower far off.
  { kind: 'ruin_colonnade', pos: { x: 880, y: 300 }, facing: Math.PI },
  { kind: 'ruin_arch', pos: { x: -16, y: 360 }, facing: 0 },
  { kind: 'ruin_tower', pos: { x: 640, y: 830 }, facing: SOUTH },
  { kind: 'windmill', pos: { x: 120, y: -10 }, facing: NORTH, scale: 1.4 },
  // +detail: the points of interest, the farm's yards and fields.
  ...poiProps(),
  ...farmProps(),
  // The Tower on the horizon (north-east).
  { kind: 'tower_landmark', pos: { x: r1(ZONE_W / 2 + Math.cos(Math.PI / 4) * 900), y: r1(ZONE_H / 2 + Math.sin(Math.PI / 4) * 900) }, facing: r3(Math.PI / 4 + Math.PI), scale: 1 },
];

/** +detail: Brookside Farm and the Boar Run: scarecrows, hay, crates, carts, fenced fields of barley. */
function farmProps(): PropHint[] {
  const out: PropHint[] = [];
  const F = (dx: number, dy: number): Vec2 => ({ x: r1(FARM.x + dx), y: r1(FARM.y + dy) });
  out.push({ kind: 'scarecrow', pos: F(-22, -6), facing: 0.4 }, { kind: 'scarecrow', pos: F(16, 20), facing: -2.2 }, { kind: 'scarecrow', pos: { x: BOAR_RUN.x - 30, y: BOAR_RUN.y + 26 }, facing: 1 });
  out.push({ kind: 'crop_rows', pos: F(-24, -10), scale: 7 }, { kind: 'crop_rows', pos: F(18, 24), scale: 6 }, { kind: 'crop_rows', pos: { x: BOAR_RUN.x - 34, y: BOAR_RUN.y + 20 }, scale: 8 });
  out.push({ kind: 'fence', pos: F(-24, 0), facing: 0, scale: 3 }, { kind: 'fence', pos: F(-33, -10), facing: NORTH, scale: 2.5 }, { kind: 'fence', pos: F(26, 22), facing: NORTH, scale: 2.5 }, { kind: 'fence', pos: F(18, 31), facing: 0, scale: 2.5 });
  out.push({ kind: 'hay_bale', pos: F(-2, 14) }, { kind: 'hay_bale', pos: F(1, 15.5) }, { kind: 'hay_bale', pos: F(10, -14) }, { kind: 'hay_bale', pos: F(-14, 16) });
  out.push({ kind: 'crates', pos: F(-12, 3) }, { kind: 'barrel', pos: F(-10, 1) }, { kind: 'barrel', pos: F(-1, 4) }, { kind: 'cart', pos: F(6, 16), facing: -0.4 }, { kind: 'woodpile', pos: F(-14, 10), facing: SOUTH });
  out.push({ kind: 'wildflowers', pos: F(-6, -24), scale: 6 }, { kind: 'wildflowers', pos: { x: BOAR_RUN.x + 40, y: BOAR_RUN.y - 30 }, scale: 7 });
  return out;
}

// ---------------------------------------------------------------------------
// The map
/** +ore: every rocky feature of the zone (mossvaleNodes.ts places the ore next to them). */
export const ROCK_FEATURES: readonly RockFeature[] = [
  ...ALL_BLOCKERS.filter((b) => ROCKY.test(b.kind) || RUINS.test(b.kind)).map((b) => ({ pos: P(b.at[0], b.at[1]), r: b.r, ruin: RUINS.test(b.kind) && !ROCKY.test(b.kind) })),
  ...cliffHints(cliffObstacles()).map((h) => ({ pos: h.pos, r: 4, ruin: false })),
  ...ridgeHints().map((h) => ({ pos: h.pos, r: 2.6, ruin: false })),
];

// ---------------------------------------------------------------------------

const FIELD: Omit<FieldDef, 'partyAuras' | 'loot'> = {
  kind: 'field',
  zoneId: ZONE.mossvale,
  levelBand: [1, 10],
  spawns: SPAWNS,
  portals: PORTALS,
  npcs: NPCS,
  respawnMs: RESPAWN.normal,
  respawn: { blockYd: RESPAWN.blockYd, blockMaxMs: RESPAWN.blockMaxMs, quicken: { ...RESPAWN.quicken } },
  areas: AREAS,
  secrets: SECRETS,
  waystones: WAYSTONES,
  dormantYd: 150,
  leashRadius: 30,
  theme: 'meadow',
};

/** +glide: how far a blocker stands above the ground (a glider clears it above this). */
function blockerTop(kind: string): number {
  if (/^(tree|tree_clump)$/.test(kind)) return 11;
  if (kind === 'eldertree') return 26;
  if (/windmill|watchtower|ruin_tower/.test(kind)) return 12;
  if (/cottage|forge|hermit_hut|barrow_mound|ruin_colonnade/.test(kind)) return 8;
  if (/boulder|standing_stone|menhir|statue|spoil|cliff|ruin_wall|ruin_pillar/.test(kind)) return 5;
  return 4;
}

/** Builds the zone's DungeonDef (FIELD_AURAS / FIELD_LOOT come from fields.ts). */
export function buildMossvaleZone(extra: Pick<FieldDef, 'partyAuras' | 'loot'>): DungeonDef {
  return {
    id: MAP.mossvale,
    name: 'Mossvale',
    timerMs: 0,
    forcesRequired: 0,
    entrance: ZONE_START,
    entranceFacing: r3(angleTo(ZONE_START, TOWER_FAR)),
    rooms: [{ id: 'mossvale', name: 'Mossvale', shape: BOUNDARY }],
    corridors: [],
    obstacles: [lakeObstacle(), ...riverObstacles(), ...cliffObstacles(), ...RIDGES, ...ALL_BLOCKERS.map((b) => b.shape ?? octagon(P(b.at[0], b.at[1]), b.r))],
    // +glide: free flight: the floor, how tall each obstacle stands (terrain-only for water, cliffs; props by kind), the sky puzzles.
    airspace: {
      floor: MAP.mossvale,
      obstacleTops: [0, ...riverObstacles().map(() => 0), ...cliffObstacles().map(() => 0), ...RIDGES.map(() => 5), ...ALL_BLOCKERS.map((b) => blockerTop(b.kind))],
      platforms: SKY.platforms,
      updrafts: SKY.updrafts,
      puzzles: SKY.puzzles,
    },
    props: PROPS,
    packs: [],
    route: [],
    requiredEncounters: [],
    bounds: { x: -24, y: -24, w: ZONE_W + 48, h: ZONE_H + 48 },
    field: { ...FIELD, ...extra },
  };
}

/** Zone landmarks other modules (quests, tests, the harness, the renderer) point at. */
export const ZONE_SPOTS = {
  town: TOWN,
  elder: ELDER_POS,
  start: ZONE_START,
  townArrive: TOWN_ARRIVE,
  pell: PELL_POS,
  pellHome: PELL_HOME,
  towerFar: TOWER_FAR,
  slime: SLIME,
  boarRun: BOAR_RUN,
  barrowMouth: BARROW_MOUTH,
  barrowfield: BARROWFIELD,
  farm: FARM,
  outpost: OUTPOST,
  heights: HEIGHTS,
  brambleEdge: BRAMBLE_EDGE,
  brambleDeep: BRAMBLE_DEEP,
  camp: CAMP,
  mine: MINE,
  grotto: GROTTO,
  lookout: LOOKOUT,
  rise: HILL.c,
  towerGate: TOWER_GATE,
  lake: LAKE.c,
  isle: LAKE.isle,
  falls: { x: FALLS.x, y: FALLS.y },
  crags: CRAGS,
} as const;

export { mossvaleHeight };
